import {
  Activity,
  ArrowRight,
  BookOpenCheck,
  CircleAlert,
  FileCheck2,
  FolderSearch2,
  History,
  Plus,
  ShieldEllipsis,
  ShieldAlert,
  Sparkles,
  Zap,
} from "lucide-react";
import { assessProject, riskCompletionIssues } from "../lib/assessmentEngine";
import type { ProjectWorkspace, WorkspaceMetrics } from "../types/domain";
import type { Section } from "../components/Shell";
import { StatusPill } from "../components/Ui";
import { RiskMatrix } from "../components/RiskMatrix";

interface OverviewProps {
  workspace: ProjectWorkspace;
  metrics: WorkspaceMetrics;
  onNavigate: (section: Section) => void;
  onLoadDemo: () => void;
  onStartFresh: () => void;
}

export function OverviewView({
  workspace,
  metrics,
  onNavigate,
  onLoadDemo,
  onStartFresh,
}: OverviewProps) {
  const assessment = assessProject(workspace.project);
  const projectStarted = Boolean(workspace.project.name.trim());
  const incompleteRisks = workspace.risks.filter((risk) => riskCompletionIssues(risk).length > 0);

  if (!projectStarted) {
    return (
      <section className="welcome-panel">
        <div className="welcome-copy">
          <span className="feature-kicker">
            <Sparkles size={16} />
            Enterprise Safety Assurance
          </span>
          <h2>Live Supply Chain & Design Risk Gateway.</h2>
          <p>
            Experience the predictive power of a unified risk graph. Design Safety MY prevents 
            unauthorized work by directly linking supply chain compliance to field permits and 
            dynamic HIRARC models.
          </p>
          <div className="button-row">
            <button className="button button-primary" onClick={onStartFresh}>
              <Plus size={18} />
              Start a project
            </button>
            <button className="button button-secondary" onClick={onLoadDemo}>
              Load KL Sentral Enterprise Demo
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
        <div className="assurance-preview" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
           <RiskMatrix risks={workspace.risks} hirarc={workspace.hirarcAssessments} />
        </div>
      </section>
    );
  }

  const nextActions = [
    ...assessment.warnings.map((warning) => ({
      title: warning,
      detail: "Confirm the project intake before relying on the duty map.",
      section: "project" as Section,
      severity: "danger",
    })),
    ...(metrics.nonCompliantContractors > 0
      ? [
          {
            title: `${metrics.nonCompliantContractors} contractor(s) non-compliant`,
            detail: "Immediate supply chain risk. Associated HIRARC controls and PTWs are locked.",
            section: "contractors" as Section,
            severity: "danger",
          },
        ]
      : []),
    ...(metrics.unapprovedRisks > 0
      ? [
          {
            title: `${metrics.unapprovedRisks} design risk${metrics.unapprovedRisks === 1 ? "" : "s"} awaiting approval`,
            detail: "A named reviewer and review date are required for approval.",
            section: "risks" as Section,
            severity: "warning",
          },
        ]
      : []),
    ...(metrics.openDutyEvidence > 0
      ? [
          {
            title: `${metrics.openDutyEvidence} expected evidence item${metrics.openDutyEvidence === 1 ? "" : "s"} not yet matched`,
            detail: "Review the evidence file and connect controlled project records.",
            section: "evidence" as Section,
            severity: "warning",
          },
        ]
      : []),
    ...(metrics.hirarcHighRisks > 0
      ? [{
          title: `${metrics.hirarcHighRisks} high residual HIRARC risk${metrics.hirarcHighRisks === 1 ? "" : "s"}`,
          detail: "High risk requires immediate action and accountable control verification.",
          section: "hirarc" as Section,
          severity: "danger",
        }]
      : []),
  ].slice(0, 8);

  const isPredictiveThreat = metrics.nonCompliantContractors > 0 && metrics.hirarcHighRisks > 0;

  return (
    <>
      {isPredictiveThreat && (
        <div className="alert-banner error-banner" style={{ margin: "0 0 1.5rem 0", background: "var(--red-soft)", padding: "1rem", borderRadius: "var(--radius-md)", border: "1px solid var(--red)", display: "flex", gap: "1rem", alignItems: "center" }}>
          <Zap color="var(--red)" size={32} />
          <div>
            <strong style={{ display: "block", color: "var(--red)", fontSize: "1.1rem" }}>Predictive Threat Level: CRITICAL</strong>
            <p style={{ margin: 0, color: "var(--red)" }}>
              Our AI engine has detected overlapping risks. You currently have <strong>{metrics.hirarcHighRisks} high-risk activities</strong> active, and <strong>{metrics.nonCompliantContractors} non-compliant contractors</strong> in the supply chain. Field Verification and PTW systems are temporarily locked to prevent unauthorized work.
            </p>
          </div>
          <button className="button" style={{ marginLeft: "auto", background: "var(--red)", color: "white", border: "none" }} onClick={() => onNavigate("contractors")}>
            Review Supply Chain
          </button>
        </div>
      )}

      <section className="two-column" style={{ marginBottom: "1.5rem" }}>
        <article className="panel">
          <div className="panel-header">
            <div>
              <span className="eyebrow">Executive Analytics</span>
              <h2>Project Risk Matrix</h2>
            </div>
            <StatusPill status={metrics.hirarcHighRisks > 0 ? "warning" : "success"}>
              {metrics.hirarcHighRisks > 0 ? "High Risk Present" : "Acceptable Risk Profile"}
            </StatusPill>
          </div>
          <div style={{ padding: "1rem", display: "flex", justifyContent: "center" }}>
            <RiskMatrix risks={workspace.risks} hirarc={workspace.hirarcAssessments} />
          </div>
        </article>

        <article className="panel">
          <div className="panel-header">
            <div>
              <span className="eyebrow">Priority queue</span>
              <h2>What needs attention</h2>
            </div>
            <StatusPill status={nextActions.length ? "warning" : "success"}>
              {nextActions.length ? `${nextActions.length} priorities` : "No current gaps"}
            </StatusPill>
          </div>
          <div className="action-list">
            {nextActions.length === 0 ? (
              <div className="all-clear">
                <FileCheck2 size={28} />
                <span>
                  <strong>No current priority gaps</strong>
                  <small>Continue routine review and evidence verification.</small>
                </span>
              </div>
            ) : (
              nextActions.map((action, index) => (
                <button key={`${action.title}-${index}`} onClick={() => onNavigate(action.section)}>
                  <span className={`action-severity severity-${action.severity}`}>
                    <CircleAlert size={18} />
                  </span>
                  <span>
                    <strong>{action.title}</strong>
                    <small>{action.detail}</small>
                  </span>
                  <ArrowRight size={17} />
                </button>
              ))
            )}
          </div>
        </article>
      </section>

      <section className="metric-grid">
        <article className="metric-card metric-card-score">
          <span className="metric-icon icon-teal">
            <ShieldAlert size={21} />
          </span>
          <small>Assurance score</small>
          <strong>{metrics.assuranceScore}%</strong>
          <p>Evidence, decisions, duties and critical controls</p>
        </article>
        <article className="metric-card">
          <span className="metric-icon icon-amber">
            <ShieldEllipsis size={21} />
          </span>
          <small>Design risks</small>
          <strong>{metrics.riskCount}</strong>
          <p>{metrics.unapprovedRisks} still need approval</p>
        </article>
        <article className="metric-card">
          <span className="metric-icon icon-red">
            <Activity size={21} />
          </span>
          <small>High HIRARC risks</small>
          <strong>{metrics.hirarcHighRisks}</strong>
          <p>{metrics.controlsDue} critical controls need attention</p>
        </article>
        <article className="metric-card">
          <span className="metric-icon icon-red">
            <FolderSearch2 size={21} />
          </span>
          <small>Supply Chain Violations</small>
          <strong>{metrics.nonCompliantContractors}</strong>
          <p>Requires immediate attention</p>
        </article>
      </section>

      <section className="panel audit-panel">
        <div className="panel-header">
          <div>
            <span className="eyebrow">Accountable history</span>
            <h2>Recent assurance activity</h2>
          </div>
          <StatusPill status="neutral">{workspace.auditTrail.length} recorded events</StatusPill>
        </div>
        {workspace.auditTrail.length === 0 ? (
          <div className="all-clear">
            <History size={25} />
            <span>
              <strong>No activity recorded yet</strong>
              <small>Changes to project facts and assurance records will appear here.</small>
            </span>
          </div>
        ) : (
          <div className="audit-list">
            {[...workspace.auditTrail]
              .sort((a, b) => b.occurredAt.localeCompare(a.occurredAt))
              .slice(0, 8)
              .map((event) => (
                <div key={event.id}>
                  <span className="audit-icon"><History size={15} /></span>
                  <div>
                    <strong>{event.summary}</strong>
                    <small>{event.actor} - {new Date(event.occurredAt).toLocaleString("en-MY")}</small>
                  </div>
                  <code>{event.action}</code>
                </div>
              ))}
          </div>
        )}
      </section>
    </>
  );
}
